/**
 * Result alert section for XeroManualForm (success / orphaned warning).
 *
 * Extracted to reduce complexity of the parent form component.
 */

import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle, Plus } from 'lucide-react'
import type { ImportResponse } from '../../types'

interface XeroResultAlertProps {
  result: ImportResponse
  xeroOrganisationId: string
  onCreateOrganisation?: ((xeroOrgId?: string) => void) | undefined
}

export function XeroResultAlert({
  result,
  xeroOrganisationId,
  onCreateOrganisation,
}: XeroResultAlertProps): React.ReactElement {
  const { t } = useTranslation('admin')

  if (result.message.includes('WARNING:ORPHANED')) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
        <AlertCircle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
            {t('licensing.xero.invoiceSavedSuccess')}
          </p>
          <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
            {t('licensing.xero.noOrgFoundWarning')}
          </p>
          {onCreateOrganisation && (
            <button
              type="button"
              onClick={(): void => {
                onCreateOrganisation(xeroOrganisationId || undefined)
              }}
              className="mt-3 flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-amber-700"
            >
              <Plus className="h-4 w-4" />
              {t('licensing.xero.createOrgWithXeroId')}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-start gap-3 rounded-lg border border-[#4e5e82]/15 bg-[#4e5e82]/5 p-4 dark:border-[#4e5e82] dark:bg-[#8a9bba]/10">
      <CheckCircle className="h-5 w-5 shrink-0 text-[#5c6e94] dark:text-[#8a9bba]" />
      <p className="text-sm text-[#5c6e94] dark:text-[#8a9bba]">{result.message}</p>
    </div>
  )
}
